import { Icon } from "Components";
import { useEffect, useMemo, useState } from "react";
import {
    Input,
    Select,
    Row,
    Col,
    Pagination,
    Spin,
} from "antd";
import TemplateCard from "./TemplateCard";
import { DEFAULT_TEMPLATES_PAGESIZE } from "../PhishingManager.constants";
import { CampaignThresholdData } from "../PhishingManager";
import moment from "moment";
import ThreeMonthWhitelistingModal from "./Modals/ThreeMonthWhitelistingModal";
import FirstWhitelistingModal from "./Modals/FirstWhitelistingModal";
import { filterResults } from "../CampaignSetup";
import { getPhishingCampaignTemplates } from "Services/Api/PhishingManager/PhishingManager.Service";
import { debounce } from "lodash";

type Step1Props = {
    selectedTemplate: string | number | null;
    tenantId: string | number | undefined;
    onSelect: (value: string | number | null) => void;
    onNext: () => void;
    onClose: () => void;
    thresholdData: null | CampaignThresholdData;
    filterResults: filterResults | null,
};

interface dropdownProps {
    value: string | number;
    label: string
}

export interface Template {
    value: string | number;
    label: string;
    categories: string[];
    languages: string[];
    difficulty: string
}

export default function Step1({ selectedTemplate, filterResults, onSelect, onNext, thresholdData, tenantId }: Step1Props) {
    const [search, setSearch] = useState<string>("");
    const [languages, setLanguages] = useState<dropdownProps[]>([]);
    const [categories, setCategories] = useState<dropdownProps[]>([]);
    const [difficulties, setDifficulties] = useState<dropdownProps[]>([]);
    const [language, setLanguage] = useState<string>("");
    const [category, setCategory] = useState<string>("");
    const [difficulty, setDifficulty] = useState<string>("");
    const [templatePage, setTemplatePage] = useState(1);
    const pageSize = DEFAULT_TEMPLATES_PAGESIZE;
    const [openFirstWhitelistingModal, setOpenFirstWhitelistingModal] = useState(false);
    const [openThreeMonthWhitelistingModal, setOpenThreeMonthWhitelistingModal] = useState(false);
    const [hasShownWhitelisting, setHasShownWhitelisting] = useState(false);
    const [templates, setTemplates] = useState<any[]>([]);
    const [totalTemplates, setTotalTemplates] = useState(0);
    const [loading, setLoading] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearch(value);
            }, 500),
        []
    );

    const handleCloseFirstModal = () => {
        setOpenFirstWhitelistingModal(false);
    };

    const fetchCampaignsTemplates = async () => {
        try {
            const hasFilters =
                !!difficulty ||
                (selectedLanguageIds?.length ?? 0) > 0 ||
                (selectedCategoryIds?.length ?? 0) > 0;
            if (search && search.length <= 2 && !hasFilters) {
                return;
            }
            setLoading(true);
            const response = await getPhishingCampaignTemplates(tenantId, {
                name: search && search.length > 2 ? search : undefined,
                difficulties: difficulty ? [difficulty] : undefined,
                languages: selectedLanguageIds,
                categories: selectedCategoryIds,
                page: templatePage,
                limit: pageSize,
            });

            setTemplates(
                response.data.map((el: any) => ({
                    value: el.id,
                    label: el.name,
                    categories: el.categories,
                    languages: el.languages,
                    difficulty: el.difficulties,
                    img: el.img_path,
                }))
            );

            setTotalTemplates(response.meta.total);
        } catch (e) {
            setTemplates([]);
            setTotalTemplates(0);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseThreeMonthModal = () => {
        setOpenThreeMonthWhitelistingModal(false);
    };

    const paginationItemRender = (
        page: number,
        type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
        originalElement: React.ReactNode
    ) => {
        if (type === "prev") {
            return (
                <span className="ml-pagination-btn">
                    <Icon type="previousPagination" />
                </span>
            );
        }

        if (type === "next") {
            return (
                <span className="ml-pagination-btn">
                    <Icon type="nextPagination" />
                </span>
            );
        }

        return originalElement;
    };

    const isExactlyAfterThreeMonths = (firstDate: Date) => {
        const target = moment(firstDate).add(3, 'months');
        return moment().isSame(target, 'day');
    };

    const dropdownIcon = (
        <div className="cs-dropdownWrapper">
            <Icon type="campaignSetupDropdown" />
        </div>
    );

    useEffect(() => {
        setTemplatePage(1);
        fetchCampaignsTemplates();
    }, [debouncedSearch, category, difficulty, language]);


    const selectedLanguageIds = filterResults?.languages
        ?.filter(l => l.name === language)
        .map(l => l.id);

    const selectedCategoryIds = filterResults?.categories
        ?.filter(c => c.name === category)
        .map(c => c.id);

    useEffect(() => {
        if (!!filterResults) {
            setLanguages([{ value: "", label: "Select a language" }, ...filterResults?.languages?.map((el: { id: number, name: string }) => { return { value: el.name, label: el.name } })]);
            setDifficulties([
                { value: "", label: "Select a difficulty" },
                ...(filterResults?.difficulties ?? []).map((el: string) => {
                    const formatted =
                        el.charAt(0).toUpperCase() + el.slice(1).toLowerCase();

                    return {
                        value: formatted,
                        label: formatted,
                    };
                }),
            ]);
            setCategories([{ value: "", label: "Select a category" }, ...filterResults?.categories.map((el: { id: number, name: string }) => { return { value: el.name, label: el.name } })]);
        }
    }, [filterResults]);

    useEffect(() => {
        if (!thresholdData || hasShownWhitelisting) return;
        const { first_campaign_created_at } = thresholdData;
        setOpenFirstWhitelistingModal(false);
        setOpenThreeMonthWhitelistingModal(false);
        if (!first_campaign_created_at) {
            setOpenFirstWhitelistingModal(true);
            setHasShownWhitelisting(true);
            return;
        }
        const firstCampaignDate = new Date(first_campaign_created_at);
        if (isExactlyAfterThreeMonths(firstCampaignDate)) {
            setOpenThreeMonthWhitelistingModal(true);
            setHasShownWhitelisting(true);
        }
    }, [thresholdData, hasShownWhitelisting]);

    useEffect(() => {
        fetchCampaignsTemplates();
    }, [templatePage]);

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [debouncedSetSearch]);

    return (
        <Spin spinning={loading}>
            <div className="campaign-template-list">
                <FirstWhitelistingModal
                    open={openFirstWhitelistingModal}
                    onClose={handleCloseFirstModal}
                />
                <ThreeMonthWhitelistingModal
                    open={openThreeMonthWhitelistingModal}
                    onClose={handleCloseThreeMonthModal}
                />
                <div>
                    <div className="campaign-scenario">Scenario</div>
                    <div className="campaign-scenario-subtitle">
                        Choose the scenario for the campaign
                    </div>
                </div>

                <div className="d-flex gap-24 justify-between flex-wrap align-center">
                    <Input
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearch(value);           
                            debouncedSetSearch(value);
                        }} suffix={<Icon type="campaignSetupSearch" />}
                        className="campaign-setup-search"
                    />

                    <Select
                        className="ml-select campaign-setup-select"
                        dropdownClassName="campaign-setup-filter-option-select"
                        suffixIcon={dropdownIcon}
                        value={language}
                        onChange={(value) => setLanguage(value)}
                        options={languages}
                    />

                    <Select
                        className="ml-select campaign-setup-select"
                        dropdownClassName="campaign-setup-filter-option-select"
                        suffixIcon={dropdownIcon}
                        value={category}
                        onChange={(value) => setCategory(value)}
                        options={categories}
                    />

                    <Select
                        className="ml-select campaign-setup-select"
                        dropdownClassName="campaign-setup-filter-option-select"
                        suffixIcon={dropdownIcon}
                        value={difficulty}
                        onChange={(value) => setDifficulty(value)}
                        options={difficulties}
                    />
                </div>

                <div className="cs-template-list-header">
                    Select template ({totalTemplates} results)
                </div>

                {templates.length === 0 ? (
                    <div className="cs-template-empty-state">
                        No templates match your filters.
                    </div>
                ) : (
                    <Row gutter={[20, 20]}>
                        {templates.map((t) => (
                            <Col key={t.value} xs={24} sm={12} md={8} lg={6}>
                                <TemplateCard
                                    template={t}
                                    selected={selectedTemplate === t.value}
                                    onSelect={onSelect}
                                />
                            </Col>
                        ))}
                    </Row>
                )}
                {totalTemplates > pageSize && (
                    <div className="pm-pagination-wrapper d-flex justify-center mt-40">
                        <Pagination
                            itemRender={paginationItemRender}
                            current={templatePage}
                            total={totalTemplates}
                            pageSize={pageSize}
                            onChange={(page) => setTemplatePage(page)}
                            showSizeChanger={false}
                        />
                    </div>
                )}

                <div className="d-flex justify-end mtx-8">
                    <button
                        disabled={!selectedTemplate}
                        onClick={onNext}
                        style={{
                            background: selectedTemplate ? "#006CA3" : "#EAEAEB",
                            color: selectedTemplate ? "#fff" : "#999",
                            border: "none",
                            borderRadius: 8,
                            padding: "10px 32px",
                            fontWeight: 600,
                            fontSize: 15,
                            cursor: selectedTemplate ? "pointer" : "not-allowed",
                            transition: "all 0.18s",
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </Spin>
    );
}